import { useAuthStore } from "@/store/auth.store";
import { Button, Form, Grid, Input, Modal, Typography, message } from "antd";
import type { Rule } from "antd/es/form";
import { X } from "lucide-react";
import { useState } from "react";
import { UpdatePassword } from "@/services/auth.service";
import { wait } from "@/utils/wait";
const { Title } = Typography;
const { useBreakpoint } = Grid;

type ChangePasswordProps = {
  open: boolean;
  onClose: () => void;
};

type PasswordFieldName =
  | "currentPassword"
  | "newPassword"
  | "confirmNewPassword";

type PasswordFieldConfig = {
  name: PasswordFieldName;
  label: string;
  placeholder: string;
  requiredMessage: string;
};

type ChangePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

type PasswordFieldProps = {
  field: PasswordFieldConfig;
};

function ModalTitle({ onClose }: { onClose: () => void }) {
  const screens = useBreakpoint();

  return (
    <div className="flex items-center justify-between gap-4 bg-[#E5F2FF] p-6 rounded-tl-2xl rounded-tr-2xl">
      <Title
        level={2}
        style={{
          marginBottom: 0,
          fontSize: screens.xs ? 24 : 28,
          fontWeight: 700,
        }}
      >
        Tukar Kata Laluan
      </Title>
      <button type="button" className="cursor-pointer" onClick={onClose}>
        <X size={24} aria-hidden="true" />
      </button>
    </div>
  );
}

function ModalButton({
  onClose,
  isSubmitting,
}: {
  onClose: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div className="grid md:grid-cols-2 md:gap-10 gap-6">
      <Button
        htmlType="button"
        color="default"
        variant="solid"
        size="large"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Batal
      </Button>
      <Button
        type="primary"
        size="large"
        htmlType="submit"
        form="change-password-form"
        loading={isSubmitting}
      >
        Simpan
      </Button>
    </div>
  );
}

export function ChangePassword({ open, onClose }: ChangePasswordProps) {
  const [form] = Form.useForm<ChangePasswordFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFinish = async (values: ChangePasswordFormValues) => {
    if (isSubmitting) return;

    const startTime = Date.now();

    try {
      setIsSubmitting(true);

      const adminId = useAuthStore.getState().user?.adminId;
      if (!adminId) {
        throw new Error("Admin tidak dijumpai");
      }

      await UpdatePassword({
        adminId,
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      const elapsed = Date.now() - startTime;
      const minimumLoadingDuration = 500;

      if (elapsed < minimumLoadingDuration) {
        await wait(minimumLoadingDuration - elapsed);
      }

      message.success("Kata laluan berjaya dikemaskini");
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Change password failed:", error);

      message.error(
        error instanceof Error
          ? error.message
          : "Gagal mengemaskini kata laluan",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      closable={false}
      title={<ModalTitle onClose={onClose} />}
      styles={{
        container: { padding: 0 },
        header: { padding: 0, marginBottom: 0 },
        body: { padding: 24 },
        footer: { padding: "0 16px 16px" },
      }}
      width={900}
      footer={
        <ModalButton
          onClose={() => {
            form.resetFields();
            onClose();
          }}
          isSubmitting={isSubmitting}
        />
      }
      centered
    >
      <Form
        id="change-password-form"
        form={form}
        layout="vertical"
        requiredMark={false}
        scrollToFirstError
        onFinish={handleFinish}
      >
        {passwordFields.map((field) => (
          <PasswordField key={field.name} field={field} />
        ))}
      </Form>
    </Modal>
  );
}

function renderLabel(text: string, required = false) {
  return (
    <span className="text-sm text-[#666] leading-none font-semibold">
      {text}
      {required && <span className="text-red-500">*</span>}
    </span>
  );
}

function requiredRule(message: string) {
  return [{ required: true, message }];
}

function PasswordField({ field }: PasswordFieldProps) {
  const rules: Rule[] =
    field.name === "confirmNewPassword"
      ? [
          ...requiredRule(field.requiredMessage),
          ({
            getFieldValue,
          }: {
            getFieldValue: (name: PasswordFieldName) => string;
          }) => ({
            validator(_: unknown, value: string) {
              if (!value || getFieldValue("newPassword") === value) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error(
                  "Sahkan kata laluan mesti sama dengan kata laluan baharu",
                ),
              );
            },
          }),
        ]
      : requiredRule(field.requiredMessage);

  return (
    <Form.Item
      label={renderLabel(field.label, true)}
      name={field.name}
      dependencies={
        field.name === "confirmNewPassword" ? ["newPassword"] : undefined
      }
      rules={rules}
    >
      <Input.Password placeholder={field.placeholder} size="large" />
    </Form.Item>
  );
}

const passwordFields: PasswordFieldConfig[] = [
  {
    name: "currentPassword",
    label: "Kata Laluan Semasa",
    placeholder: "Kata laluan semasa",
    requiredMessage: "Sila masukkan kata laluan semasa",
  },
  {
    name: "newPassword",
    label: "Kata Laluan Baharu",
    placeholder: "Kata laluan baharu",
    requiredMessage: "Sila masukkan kata laluan baharu",
  },
  {
    name: "confirmNewPassword",
    label: "Sahkan Kata Laluan Baharu",
    placeholder: "Sahkan kata laluan baharu",
    requiredMessage: "Sila sahkan kata laluan baharu",
  },
];
